import { useCallback, useMemo, useRef } from 'react';
// redux
import { useDispatch, useSelector } from 'src/redux/store';
import {
  resetInvoices,
  updateInvoices,
  fetchOneInvoice,
  fetchTransactions,
  removeInvoice,
  addDraft,
  removeDraft,
  updateDraft,
  addSync,
  removeSync,
  updateSync,
} from 'src/redux/slice/invoice';
// auth
import { useAuthContext } from 'src/auth/hooks';
// utils
import { createPdfUrl, createPdfBlob } from 'src/utils/blob-utils';

// ----------------------------------------------------------------------

export default function useInvoices() {
  const dispatch = useDispatch();
  const { user } = useAuthContext();

  const {
    allData: invoices,
    syncs,
    drafts,
    isLoading,
    error,
  } = useSelector((state) => state.invoices);

  const tax = user?.configSelected?.tax;
  const config = user?.configSelected?.id;
  const taxIncludedInItemPrice = user?.configSelected?.taxIncludedInItemPrice;

  const invoicesRef = useRef(invoices);
  invoicesRef.current = invoices;

  // Process invoices with PDF URLs and calculated prices
  const processedInvoices = useMemo(() => {
    if (!invoices?.length) return [];

    return invoices.map((inv) => {
      const pdfBase64 = inv.pdfBase64 || inv.dataERP?.pdfBase64;
      const urlPDF = pdfBase64 ? createPdfUrl(pdfBase64) : '';
      const items = inv.items?.map((item) => {
        const priceWithTax = taxIncludedInItemPrice
          ? item.price * (1 + (tax || 0) / 100)
          : item.price;
        return { ...item, priceWithTax };
      }) || [];
      return { ...inv, urlPDF, items };
    });
  }, [invoices, tax, taxIncludedInItemPrice]);

  // Combine processed invoices with drafts
  const allInvoices = useMemo(
    () => [...processedInvoices, ...drafts],
    [processedInvoices, drafts]
  );

  // Compute unique statuses
  const allStatus = useMemo(() => {
    const statuses = allInvoices.reduce((prev, curr) => {
      const status = curr.dataERP?.status;
      if (status?.value && !prev.find((x) => x.value === status.value)) {
        prev.push(status);
      }
      return prev;
    }, []);
    return statuses.length > 0 ? statuses : [{ value: '', label: '' }];
  }, [allInvoices]);

  const onRefreshInvoices = useCallback(async (options = {}) => {
    const includeCancelled = options?.includeCancelled === true;
    const fetchPage = async (page) => {
      const result = await dispatch(
        fetchTransactions({ idUser: user?.id, config, page, includeCancelled })
      ).unwrap();
      if (result.nextPage) {
        await fetchPage(result.nextPage);
      }
    };
    await fetchPage(1);
  }, [dispatch, user?.id, config]);

  const onRefreshOneInvoice = useCallback(
    async (id) =>
      dispatch(
        fetchOneInvoice({
          idUser: user?.id,
          config,
          id,
        })
      ).unwrap(),
    [dispatch, user?.id, config]
  );

  const onInitInvoices = useCallback(() => {
    if (!invoicesRef.current?.length) {
      onRefreshInvoices().then();
    }
  }, [onRefreshInvoices]);

  const onResetInvoices = useCallback(() => {
    dispatch(resetInvoices());
  }, [dispatch]);

  const onAddInvoice = useCallback(
    (invoice) => dispatch(updateInvoices({ data: [invoice] })),
    [dispatch]
  );

  const onRemoveInvoice = useCallback(
    (idInvoice) => dispatch(removeInvoice(idInvoice)),
    [dispatch]
  );

  const onUpdateInvoices = useCallback(
    (data) => {
      dispatch(updateInvoices({ data }));
    },
    [dispatch]
  );

  const onAddDraft = useCallback(
    (invoice) => dispatch(addDraft(invoice)),
    [dispatch]
  );

  const onRemoveDraft = useCallback(
    (idDraft) => dispatch(removeDraft(idDraft)),
    [dispatch]
  );

  const onUpdateDraft = useCallback(
    (data) => dispatch(updateDraft(data)),
    [dispatch]
  );

  const onAddSync = useCallback(
    (invoice) => dispatch(addSync(invoice)),
    [dispatch]
  );

  const onRemoveSync = useCallback(
    (idSync) => dispatch(removeSync(idSync)),
    [dispatch]
  );

  const onUpdateSync = useCallback(
    (data) => dispatch(updateSync(data)),
    [dispatch]
  );

  const getPdfBlob = useCallback((invoice) => {
    const pdfBase64 = invoice?.pdfBase64 || invoice?.dataERP?.pdfBase64;
    return pdfBase64 ? createPdfBlob(pdfBase64) : null;
  }, []);

  return useMemo(
    () => ({
      allInvoices,
      isLoading,
      allStatus,
      drafts,
      syncs,
      error,
      getPdfBlob,
      onAddDraft,
      onAddInvoice,
      onRemoveDraft,
      onUpdateDraft,
      onInitInvoices,
      onResetInvoices,
      onRemoveInvoice,
      onUpdateInvoices,
      onRefreshInvoices,
      onRefreshOneInvoice,
      onAddSync,
      onRemoveSync,
      onUpdateSync,
    }),
    [
      allInvoices,
      isLoading,
      allStatus,
      drafts,
      syncs,
      error,
      getPdfBlob,
      onAddDraft,
      onAddInvoice,
      onRemoveDraft,
      onUpdateDraft,
      onInitInvoices,
      onResetInvoices,
      onRemoveInvoice,
      onUpdateInvoices,
      onRefreshInvoices,
      onRefreshOneInvoice,
      onAddSync,
      onRemoveSync,
      onUpdateSync,
    ]
  );
}
